// ============================================================
// cf-listcard.js � CF.listCard � Reusable List Card Component
// Expandable/collapsible cards with embedded tables for
// grouped key-value data display.
//
// cshtml owns:
//   .lc-container          � outer wrapper
//   .lc-toolbar            � search, expand/collapse buttons, summary
//   .lc-search-input       � search input
//   .lc-btn-expand         � expand all button
//   .lc-btn-collapse       � collapse all button
//   .lc-summary            � summary bar (content updated by JS)
//   .lc-card-list          � card list container (content rendered by JS)
//
// JS (page script) owns:
//   options.groupMeta      � per-group metadata { GroupName: { icon, color, description } }
//   Wire search, expand/collapse behavior
//   Render cards into .lc-card-list
//   Update .lc-summary content
//
// Dependencies: site.js (CF.toast)
// ============================================================

window.CF = window.CF || {};

CF.listCard = (function () {
    'use strict';

    // ?? Inject skeleton cards into .lc-card-list ????????????
    // Called before data loads to show shimmer placeholder.
    // Mimics lc-card header structure. Uses cf-sk class for
    // easy cleanup when _renderCards() replaces innerHTML.
    function _injectSkeleton(cardList) {
        if (!cardList) return;
        var widths = [120, 100, 80, 140, 60];
        var descWidths = [200, 180, 160, 220, 190];
        var html = '';
        for (var i = 0; i < 5; i++) {
            html += '<div class="cf-sk lc-card">' +
                '<div class="lc-card-header">' +
                    '<div class="skeleton" style="width:40px;height:40px;border-radius:10px"></div>' +
                    '<div style="flex:1;display:flex;flex-direction:column;gap:6px">' +
                        '<div class="skeleton" style="height:14px;width:' + widths[i] + 'px"></div>' +
                        '<div class="skeleton" style="height:10px;width:' + descWidths[i] + 'px"></div>' +
                    '</div>' +
                    '<div class="skeleton" style="height:12px;width:50px"></div>' +
                '</div>' +
            '</div>';
        }
        cardList.innerHTML = html;
    }

    /**
     * Inject skeleton shimmer into a list card container.
     * Call before data loads (before init).
     *
     * @param {string} selector � Container element selector (.lc-container)
     */
    function showSkeleton(selector) {
        var container = document.querySelector(selector);
        if (!container) return;
        var cardList = container.querySelector('.lc-card-list');
        _injectSkeleton(cardList);
    }

    /**
     * Initialize a list card container.
     *
     * @param {string} selector � Container element selector (.lc-container)
     * @param {object} options
     *   @param {Array} options.data � Flat array of items
     *   @param {string} options.groupField � Field name to group by
     *   @param {object} [options.groupMeta] � Per-group metadata: { GroupName: { icon, color, description } }
     *   @param {Array} options.columns � Column definitions: [{ field, title, width, render, action }]
     *   @param {function} [options.onSearch] � Custom search callback (query, data) => filteredData
     *   @param {function} [options.summaryFn] � Custom summary callback (data) => HTML string or null
     *   @param {Array} [options.rowActions] � Row action definitions: [{ label, icon, action, danger }]
     *   @param {function} [options.onRowAction] � Row action callback (action, item)
     * @returns {object} Instance with reload(), expandAll(), collapseAll(), getContainer()
     */
    function init(selector, options) {
        var container = document.querySelector(selector);
        if (!container) return null;

        var _data = options.data || [];
        var _filteredData = _data;
        var _expandedGroups = {};
        var _searchQuery = '';
        var _groupMeta = options.groupMeta || {};

        // Locate DOM elements from cshtml
        var _searchInput = container.querySelector('.lc-search-input');
        var _btnExpand = container.querySelector('.lc-btn-expand');
        var _btnCollapse = container.querySelector('.lc-btn-collapse');
        var _summaryEl = container.querySelector('.lc-summary');
        var _cardList = container.querySelector('.lc-card-list');

        // Wire events
        if (_searchInput) {
            _searchInput.addEventListener('input', function () {
                _searchQuery = this.value;
                _applySearch();
            });
        }
        if (_btnExpand) {
            _btnExpand.addEventListener('click', function () { _expandAll(); });
        }
        if (_btnCollapse) {
            _btnCollapse.addEventListener('click', function () { _collapseAll(); });
        }

        // Initial render � clears skeleton injected by showSkeleton()
        _applySearch();

        function _applySearch() {
            var q = _searchQuery.trim().toLowerCase();
            if (!q) {
                _filteredData = _data;
            } else if (options.onSearch) {
                _filteredData = options.onSearch(q, _data);
            } else {
                _filteredData = _data.filter(function (item) {
                    for (var i = 0; i < options.columns.length; i++) {
                        var col = options.columns[i];
                        if (col.field && !col.action) {
                            var val = item[col.field];
                            if (val && String(val).toLowerCase().indexOf(q) >= 0) return true;
                        }
                    }
                    return false;
                });
            }
            _renderCards();
            _updateSummary();
        }

        function _updateSummary() {
            if (options.summaryFn) {
                var html = options.summaryFn(_filteredData);
                if (_summaryEl && html) {
                    _summaryEl.innerHTML = html;
                }
            }
        }

        function _renderCards() {
            if (!_cardList) return;
            _cardList.innerHTML = '';

            // Group data
            var groups = {};
            var groupOrder = [];
            _filteredData.forEach(function (item) {
                var g = item[options.groupField] || 'Ungrouped';
                if (!groups[g]) {
                    groups[g] = [];
                    groupOrder.push(g);
                }
                groups[g].push(item);
            });

            if (groupOrder.length === 0) {
                _cardList.innerHTML =
                    '<div class="lc-empty">' +
                    '<i class="fa-solid fa-inbox"></i>' +
                    '<span>No results found</span></div>';
                return;
            }

            groupOrder.forEach(function (groupName) {
                var items = groups[groupName];
                var meta = _groupMeta[groupName] || {};
                var isExpanded = !!_expandedGroups[groupName];

                var card = document.createElement('div');
                card.className = 'lc-card';
                card.dataset.group = groupName;

                // Card header
                var header = document.createElement('div');
                header.className = 'lc-card-header';
                header.innerHTML = _buildHeaderHtml(groupName, items.length, meta, isExpanded);
                card.appendChild(header);

                // Card body (table)
                var body = document.createElement('div');
                body.className = 'lc-card-body';
                body.style.display = isExpanded ? '' : 'none';

                // Column headers
                var colHeaders = '<div class="lc-table-header"><div class="lc-table-row">';
                options.columns.forEach(function (col) {
                    if (col.action) {
                        colHeaders += '<div class="lc-table-cell lc-cell-action" style="width:' + (col.width || '60px') + '"></div>';
                    } else {
                        colHeaders += '<div class="lc-table-cell" style="width:' + (col.width || 'auto') + '">' +
                            '<span class="lc-col-title">' + (col.title || '') + '</span></div>';
                    }
                });
                colHeaders += '</div></div>';
                body.innerHTML = colHeaders;

                // Rows
                var rowsHtml = '<div class="lc-table-body">';
                items.forEach(function (item) {
                    rowsHtml += '<div class="lc-table-row" data-id="' + (item.id || '') + '">';
                    options.columns.forEach(function (col) {
                        if (col.action) {
                            rowsHtml += _buildActionCell(item, col);
                        } else {
                            var val = item[col.field];
                            var html = col.render ? col.render(val, item) : _escHtml(val != null ? String(val) : '');
                            rowsHtml += '<div class="lc-table-cell" style="width:' + (col.width || 'auto') + '">' + html + '</div>';
                        }
                    });
                    rowsHtml += '</div>';
                });
                rowsHtml += '</div>';
                body.innerHTML += rowsHtml;

                card.appendChild(body);
                _cardList.appendChild(card);

                // Header click to toggle
                header.addEventListener('click', function () {
                    _toggleGroup(groupName);
                });

                // Wire row action clicks
                if (options.rowActions && options.onRowAction) {
                    body.querySelectorAll('.lc-row-action').forEach(function (btn) {
                        btn.addEventListener('click', function (e) {
                            e.stopPropagation();
                            var action = this.dataset.action;
                            var id = this.closest('.lc-table-row').dataset.id;
                            var item = _findItemById(id);
                            if (item) options.onRowAction(action, item);
                        });
                    });
                }
            });
        }

        function _buildHeaderHtml(groupName, count, meta, isExpanded) {
            var iconHtml = '';
            if (meta.icon) {
                var bgColor = meta.color || '#6366F1';
                iconHtml = '<div class="lc-group-icon" style="background:' + bgColor + '20;color:' + bgColor + '">' +
                    '<i class="' + meta.icon + '"></i></div>';
            } else {
                var colors = ['#6366F1', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#06B6D4', '#EC4899', '#84CC16'];
                var hash = 0;
                for (var i = 0; i < groupName.length; i++) hash = groupName.charCodeAt(i) + ((hash << 5) - hash);
                var c = colors[Math.abs(hash) % colors.length];
                var initial = groupName.charAt(0).toUpperCase();
                iconHtml = '<div class="lc-group-icon" style="background:' + c + '20;color:' + c + '">' +
                    '<span>' + initial + '</span></div>';
            }

            var descHtml = meta.description
                ? '<span class="lc-group-desc">' + _escHtml(meta.description) + '</span>'
                : '';

            var chevron = isExpanded ? 'fa-chevron-up' : 'fa-chevron-down';

            return iconHtml +
                '<div class="lc-group-info">' +
                    '<span class="lc-group-name">' + _escHtml(groupName) + '</span>' +
                    descHtml +
                '</div>' +
                '<div class="lc-header-right">' +
                    '<span class="lc-key-count">' + count + ' key' + (count !== 1 ? 's' : '') + '</span>' +
                    '<i class="fa-solid ' + chevron + ' lc-chevron"></i>' +
                '</div>';
        }

        function _buildActionCell(item, col) {
            if (!options.rowActions || options.rowActions.length === 0) return '<div class="lc-table-cell lc-cell-action"></div>';

            var html = '<div class="lc-table-cell lc-cell-action" style="width:' + (col.width || '60px') + '">';
            options.rowActions.forEach(function (ra) {
                if (ra.divider) return;
                var cls = 'lc-row-action' + (ra.danger ? ' lc-action-danger' : '');
                html += '<button class="' + cls + '" data-action="' + ra.action + '" title="' + _escHtml(ra.label) + '">' +
                    '<i class="' + ra.icon + '"></i> ' + _escHtml(ra.label) + '</button>';
            });
            html += '</div>';
            return html;
        }

        function _toggleGroup(groupName) {
            _expandedGroups[groupName] = !_expandedGroups[groupName];
            var card = _cardList.querySelector('.lc-card[data-group="' + groupName + '"]');
            if (!card) return;

            var body = card.querySelector('.lc-card-body');
            var chevron = card.querySelector('.lc-chevron');
            if (_expandedGroups[groupName]) {
                body.style.display = '';
                if (chevron) { chevron.classList.remove('fa-chevron-down'); chevron.classList.add('fa-chevron-up'); }
            } else {
                body.style.display = 'none';
                if (chevron) { chevron.classList.remove('fa-chevron-up'); chevron.classList.add('fa-chevron-down'); }
            }
        }

        function _expandAll() {
            _cardList.querySelectorAll('.lc-card').forEach(function (card) {
                var g = card.dataset.group;
                _expandedGroups[g] = true;
                var body = card.querySelector('.lc-card-body');
                var chevron = card.querySelector('.lc-chevron');
                if (body) body.style.display = '';
                if (chevron) { chevron.classList.remove('fa-chevron-down'); chevron.classList.add('fa-chevron-up'); }
            });
        }

        function _collapseAll() {
            _cardList.querySelectorAll('.lc-card').forEach(function (card) {
                var g = card.dataset.group;
                _expandedGroups[g] = false;
                var body = card.querySelector('.lc-card-body');
                var chevron = card.querySelector('.lc-chevron');
                if (body) body.style.display = 'none';
                if (chevron) { chevron.classList.remove('fa-chevron-up'); chevron.classList.add('fa-chevron-down'); }
            });
        }

        function _findItemById(id) {
            for (var i = 0; i < _data.length; i++) {
                if (String(_data[i].id) === String(id)) return _data[i];
            }
            return null;
        }

        function _escHtml(str) {
            if (!str) return '';
            var div = document.createElement('div');
            div.appendChild(document.createTextNode(str));
            return div.innerHTML;
        }

        // Public API
        return {
            reload: function (newData) {
                _data = newData || [];
                _applySearch();
            },
            expandAll: function () { _expandAll(); },
            collapseAll: function () { _collapseAll(); },
            getContainer: function () { return container; }
        };
    }

    return { init: init, showSkeleton: showSkeleton };
})();
